import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Modal, Spinner, ProgressBar } from "react-bootstrap";

const ForYouVideoForm = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const [updatedUser, setUpdatedUser] = useState({
    title: "",
    video: "",
    thumbnail: "", // Store existing thumbnail
    categories: [],
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/for-you-category/foryou-category`
        );
        if (response.data.data) {
          const options = response.data.data.map((category) => ({
            value: category.id,
            label: category.name,
          }));
          setCategoriesOptions(options);
        } else {
          toast.error("Failed to fetch categories");
        }
      } catch (error) {
        toast.error("Error fetching categories");
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (user && categoriesOptions.length > 0) {
      setUpdatedUser({
        title: user.title || "",
        video: user.video || "",
        thumbnail: user.thumbnail || "",
        categories: [],
      });

      if (Array.isArray(user.categories)) {
        const matchedCategories = user.categories
          .map((categoryName) => {
            const matchedOption = categoriesOptions.find(
              (option) => option.label === categoryName
            );
            return matchedOption || null;
          })
          .filter(Boolean);

        setSelectedCategories(matchedCategories);
        setUpdatedUser((prev) => ({
          ...prev,
          categories: matchedCategories.map((cat) => cat.value),
        }));
      }

      setVideoPreview(user.video || null);
      setThumbnailPreview(user.thumbnail || null); // Set preview
    }
  }, [user, categoriesOptions]);

  // In handleFileChange, make sure it's using a consistent value:
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Define a constant for the file size limit
      const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB in bytes

      if (file.size > MAX_FILE_SIZE) {
        toast.error('Video size must not exceed 200MB.');
        e.target.value = ''; // Reset the file input
        return;
      }

      setSelectedFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setIsEdited(true);
    }
  };

  const handleCategoryChange = (selectedOptions) => {
    setSelectedCategories(selectedOptions || []);

    const categoryIds = (selectedOptions || []).map((option) => option.value);
    setUpdatedUser((prev) => ({
      ...prev,
      categories: categoryIds,
    }));

    setIsEdited(true);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedThumbnail(file); // Store as file
      setThumbnailPreview(URL.createObjectURL(file)); // Generate preview
      setIsEdited(true);
    }
  };

  // In validateForm, use the same constant:
  const getPresignedUrl = async (file) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/for-you-category/get-presigned-url`, {
        fileName: file.name,
        fileType: file.type
      });
      return response.data;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      throw new Error('Failed to get upload URL');
    }
  };

  const uploadToS3 = async (url, file, onProgress) => {
    try {
      const contentType =
        file.type && file.type !== ""
          ? file.type
          : file.name.endsWith(".svg")
            ? "image/svg+xml"
            : "application/octet-stream";
      await axios.put(url, file, {
        headers: {
          'Content-Type': contentType,
          "x-amz-server-side-encryption": "aws:kms"
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        }
      });
    } catch (error) {
      console.error('Error uploading to S3:', error.response?.data || error.message);
      throw new Error('Failed to upload file to storage');
    }
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const validateForm = () => {
    // 500MB limit for consistency with create flow
    const MAX_FILE_SIZE = 500 * 1024 * 1024;

    if (!updatedUser.title.trim()) {
      toast.error("Title is required.");
      return false;
    }
    // For update, video is optional if already exists
    if (!selectedFile && !updatedUser.video) {
      toast.error("Video is required.");
      return false;
    }
    if (selectedCategories.length === 0) {
      toast.error("At least one category is required.");
      return false;
    }

    if (selectedFile && selectedFile.size > MAX_FILE_SIZE) {
      toast.error('Video size must not exceed 500MB.');
      return false;
    }

    return true;
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!isEdited) {
      toast.info("No changes detected. Please update before saving.");
      return;
    }



    setIsLoading(true);
    setIsUploading(true);
    setUploadProgress(0);
    setShowLoadingModal(true);

    try {
      let videoUrl = null;
      let thumbnailUrl = null;
      let duration = null;

      // 1. Handle Video Upload if new file selected
      if (selectedFile) {
        // Get duration
        duration = await getVideoDuration(selectedFile);

        // Upload
        const presigned = await getPresignedUrl(selectedFile);
        await uploadToS3(presigned.uploadUrl, selectedFile, (progress) => {
          setUploadProgress(progress);
        });
        videoUrl = presigned.publicUrl;
      }

      // 2. Handle Thumbnail Upload if new file selected
      if (selectedThumbnail) {
        const presigned = await getPresignedUrl(selectedThumbnail);
        await uploadToS3(presigned.uploadUrl, selectedThumbnail, () => { });
        thumbnailUrl = presigned.publicUrl;
      }

      // 3. Submit Update to Backend
      const payload = {
        title: updatedUser.title,
        categories: updatedUser.categories
      };

      if (videoUrl) {
        payload.videoUrl = videoUrl;
        payload.duration = duration;
      }
      if (thumbnailUrl) {
        payload.thumbnailUrl = thumbnailUrl;
      }

      const request = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/for-you-category/update-foryou-video/${id}`,
        payload
      );

      if (request.status === 200 || request.status === 201) {
        setIsLoading(false);
        setIsUploading(false);
        setShowLoadingModal(false);

        toast.success('Video updated successfully! 🎉');
        setTimeout(() => navigate('/for-you-video', {
          state: { fromUpdatePage: true }
        }), 1500);
      } else {
        throw new Error(request.data?.message || 'Unexpected response status');
      }

    } catch (error) {
      console.error('Update flow error:', error);
      setShowLoadingModal(false);
      setIsLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
      toast.error(
        error.message || "Failed to update the video."
      );
    }
  };

  // Loading Modal Component (Non-blocking - allows navigation)
  const LoadingModal = () => {
    return (
      <Modal
        show={showLoadingModal}
        centered
        backdrop={false}
        keyboard={true}
        size="sm"
      >
        <Modal.Body style={{ textAlign: 'center', padding: '30px' }}>
          <Spinner animation="border" role="status" style={{ marginBottom: '15px' }} />
          <p style={{ margin: 0, fontSize: '16px' }}>Updating video...</p>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div style={styles.container}>
      <LoadingModal />
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Update Video</h2>
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button
              onClick={onSave}
              disabled={isLoading}
              style={styles.saveButton}
            >
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        <div style={styles.formContainer}>
          <div style={styles.formField}>
            <label style={styles.label}>Title</label>
            <input
              type="text"
              name="title"
              value={updatedUser.title}
              onChange={(e) => {
                setUpdatedUser({ ...updatedUser, title: e.target.value });
                setIsEdited(true);
              }}
              style={styles.input}
              placeholder="Enter video title"
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.label}>Thumbnail</label>
            {thumbnailPreview && (
              <img
                src={thumbnailPreview}
                alt="Thumbnail Preview"
                style={styles.thumbnailPreview}
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
              style={styles.input}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.label}>Video</label>
            {videoPreview && (
              <video key={videoPreview} controls style={styles.videoPreview}>
                <source src={videoPreview} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
            <input
              type="file"
              accept="video/*"
              onChange={handleFileChange}
              style={styles.input}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.label}>Categories</label>
            <Select
              isMulti
              options={categoriesOptions}
              value={selectedCategories}
              onChange={handleCategoryChange}
              placeholder="Select categories"
              styles={{
                control: (provided) => ({
                  ...provided,
                  backgroundColor: "#F4F4F6",
                  border: "none",
                }),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "60%",
    fontFamily: "Poppins",
    display: "flex",
    justifyContent: "center",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "8px",
    width: "100%",
    maxWidth: "800px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
  },
  header: {
    backgroundColor: "#064260",
    color: "white",
    padding: "15px 30px",
    borderRadius: "8px 8px 0 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  title: { margin: 0, fontSize: "21px" },
  formContainer: {
    padding: "30px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formField: { display: "flex", flexDirection: "column", gap: "10px" },
  label: { fontWeight: "bold", fontSize: "14px" },
  input: {
    padding: "10px",
    borderRadius: "4px",
    backgroundColor: "#F4F4F6",
    border: "none",
    width: "100%",
  },
  videoPreview: { maxWidth: "70%", marginBottom: "15px" },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "20px",
  },
  saveButton: {
    backgroundColor: "#27B99A",
    border: "none",
    borderRadius: "35px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: 500,
    padding: "8px 10px",
    width: "80px",
  },
  deleteButton: {
    backgroundColor: "#dc3545",
    border: "none",
    borderRadius: "35px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: 500,
    padding: "8px 10px",
    width: "80px",
  },
  thumbnailPreview: { maxWidth: "100px", height: "auto", marginBottom: "10px" },
};

export default ForYouVideoForm;
